import React from 'react';

import {
  CapaQuotationChatMessageType,
  CapaQuotationChatMessageRfq,
  CapaQuotationChatRoomStatus,
  useAllowRfqDrawingFileDownloadMutation,
  CapaQuotationRfqAllowOpenFileStatus,
  useFetchRfqPartnersQuery,
  FetchRfqPartnersDocument
} from '@generated/graphql';

import { clsx } from '@components/Provider';
import { CustomizedTypography, CustomizedButton } from '@components/Customized';

import { Grid } from '@material-ui/core';

import useStyles from '../useStyles';
import { useSelector } from '@store';

interface Props {
  message?: CapaQuotationChatMessageRfq;
}

const RequestBubble: React.FC<Props> = ({ message }) => {
  const classes = useStyles();
  const partnerId = useSelector((state) => state.chat.quotationPartnerId);
  const chatRoomStatus = useSelector((state) => state.chat.chatRoomStatus);

  const rfqData = message?.rfq ?? undefined;
  const rfqId = rfqData?.rfqId;
  const messageType = message?.messageType ?? undefined;
  const userManagerName = rfqData?.userManagerName ?? '';
  const partnerManagerName = rfqData?.partnerManagerName ?? '';
  const partnerOrganizationName = rfqData?.partnerOrganizationName ?? '';
  const projectName = message?.rfq?.projectName ?? '';
  const titleText =
    messageType === CapaQuotationChatMessageType.AllowRequest
      ? '도면 다운로드 권한 요청'
      : messageType === CapaQuotationChatMessageType.ConfirmRequest
      ? '업체확정 요청'
      : '거래완료 확인 요청';

  const { data } = useFetchRfqPartnersQuery({
    variables: { rfqId: rfqId ?? '' },
    skip: !rfqId
  });

  const hasPermission =
    data?.fetchRfqPartners?.data?.find(
      (partner) => partner?.quotationPartnerId === partnerId
    )?.permission === CapaQuotationRfqAllowOpenFileStatus.Allow;

  const [allowRfqDrawingFileDownload] = useAllowRfqDrawingFileDownloadMutation({
    variables: {
      input: {
        quotationPartnerId: partnerId ?? '',
        rfqId: rfqData?.rfqId ?? '',
        status: CapaQuotationRfqAllowOpenFileStatus.Allow
      }
    }
  });

  const onAllow = async () => {
    await allowRfqDrawingFileDownload({
      refetchQueries: [
        {
          query: FetchRfqPartnersDocument,
          variables: { rfqId: rfqId }
        }
      ],
      awaitRefetchQueries: true
    });
  };

  return (
    <Grid
      className={clsx(classes['Chat-Message-Bubble-Common-root'], {
        owner: false
      })}
      container
    >
      <CustomizedTypography
        variant="body2"
        fontWeight="bold"
        color="primary"
        gutterBottoms={8}
      >
        {titleText}
      </CustomizedTypography>
      {messageType === CapaQuotationChatMessageType.AllowRequest && (
        <React.Fragment>
          <CustomizedTypography
            className="long-text"
            variant="body2"
            color="textPrimary"
            gutterBottoms={16}
          >
            {partnerOrganizationName ?? partnerManagerName} 파트너님이&nbsp;
            {projectName} 의 도면 다운로드 권한을 요청하였습니다.
          </CustomizedTypography>
          <CustomizedButton
            fullWidth
            variant="contained"
            color="primary"
            size="small"
            disabled={
              hasPermission ||
              chatRoomStatus !== CapaQuotationChatRoomStatus.Opened
            }
            onClick={onAllow}
          >
            권한 부여하기
          </CustomizedButton>
        </React.Fragment>
      )}

      {messageType === CapaQuotationChatMessageType.CompleteRequest && (
        <React.Fragment>
          <CustomizedTypography
            className="long-text"
            variant="body2"
            color="textPrimary"
            gutterBottoms={16}
          >
            안녕하세요 {userManagerName}님,
            <br />
            {partnerOrganizationName}의 {partnerManagerName} 님께서&nbsp;
            {projectName}의&nbsp;
            <span className="primary-text">‘거래완료'&nbsp;</span>를
            요청하셨습니다.
          </CustomizedTypography>
          <CustomizedButton
            fullWidth
            variant="contained"
            color="primary"
            size="small"
            disabled
          >
            거래완료 하기
          </CustomizedButton>
        </React.Fragment>
      )}

      {messageType === CapaQuotationChatMessageType.ConfirmRequest && (
        <React.Fragment>
          <CustomizedTypography
            className="long-text"
            variant="body2"
            color="textPrimary"
            gutterBottoms={16}
          >
            안녕하세요 {userManagerName}님,
            <br />
            {partnerOrganizationName}
            의&nbsp;
            {partnerManagerName} 님과 작업 진행을 원하실 경우&nbsp;
            <span className="primary-text">‘업체확정'&nbsp;</span>을 진행해
            주세요. 업체확정을 하게되면 다른 파트너들과의 연결은 끊어지고
            상담종료 알림이 가게 됩니다.
          </CustomizedTypography>
          <CustomizedButton
            fullWidth
            variant="contained"
            color="primary"
            size="small"
            disabled
          >
            업체확정 하기
          </CustomizedButton>
        </React.Fragment>
      )}
    </Grid>
  );
};

export default RequestBubble;
